/** Longest shop name the header shows, in visible characters. */
export const SHOP_NAME_MAX_LENGTH = 40;

/** How the embedding shop wants the Direct Sale header. */
export interface DirectSaleEmbedOptions {
  /**
   * The header row with the steps. Off (`?header=0`) leaves only a slim
   * back link from the second step on, so the customer can still return
   * to the product choice.
   */
  header: boolean;
  /** Name shown at the start of the header (`?shopName=`); none without. */
  shopName: string | null;
}

const OFF_VALUES = new Set(['0', 'false', 'no', 'none', 'off']);

/**
 * Characters that never belong in a displayed name: C0/C1 controls, and the
 * invisible format characters that reorder or hide text (bidi embeddings
 * and overrides, isolates, zero-width space and marks, word joiners, BOM).
 * The zero-width joiner (U+200D) stays: emoji sequences need it.
 */
const HIDDEN_CHARACTERS =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001f\u007f-\u009f\u200b\u200c\u200e\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g;

/**
 * Reads the embedding shop's display wishes from the iframe URL. Like the
 * colour parameters they sit outside the signature: they only change how
 * the page looks, never what it may do.
 */
export function readEmbedOptions(
  search: string = typeof window === 'undefined' ? '' : window.location.search
): DirectSaleEmbedOptions {
  const params = new URLSearchParams(search);
  const header = params.get('header');
  return {
    header: header === null || !OFF_VALUES.has(header.trim().toLowerCase()),
    shopName: cleanShopName(params.get('shopName')),
  };
}

function cleanShopName(value: string | null): string | null {
  if (value === null) return null;
  const cleaned = value.replace(HIDDEN_CHARACTERS, '').trim();
  if (cleaned.length === 0) return null;
  return firstGraphemes(cleaned, SHOP_NAME_MAX_LENGTH).trim() || null;
}

/** The first `count` visible characters, never cutting through one. */
function firstGraphemes(value: string, count: number): string {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segments = new Intl.Segmenter(undefined, {
      granularity: 'grapheme',
    }).segment(value);
    let result = '';
    let taken = 0;
    for (const { segment } of segments) {
      if (taken === count) break;
      result += segment;
      taken += 1;
    }
    return result;
  }
  // Without Segmenter: at least never split a surrogate pair.
  return [...value].slice(0, count).join('');
}
